import * as React from 'react';
import { Button, Form, Heading, Input, LayoutGrid } from '@built/design-system';

import PasswordField, { passwordValid } from '../containers/PasswordField';
import Notification from '../containers/Notification';
import ActionButton from '../containers/ActionButton';
import EmailField from '../containers/EmailField';
import Header from '../containers/Header';
import Footer from '../components/Footer';
import { AuthErrorType } from '../types';
import { getConfig } from '../utils';
import * as ErrorNotifications from './ErrorNotifications';
import * as styles from './PasswordReset.module.scss';
import { AuthError } from '../types';
import l10n from '@built/i18n';

/**
 * Renders a password reset component
 */
export default function PasswordReset(props: PasswordResetProps) {
  const { className } = props;
  const requesting = props.resetState.indexOf('request') === 0;

  const classes = [styles.root];
  if (className) {
    classes.push(className);
  }

  return (
    <React.Fragment>
      <Header />
      <LayoutGrid className={classes.join(' ')}>
        <LayoutGrid.Row center={true} className={styles.contentRow}>
          <LayoutGrid.Cell columnsMd={9} columnsXs={12}>
            <div className={styles.heading}>{renderHeading(props)}</div>

            {requesting ? renderRequest(props) : renderReset(props)}
          </LayoutGrid.Cell>
        </LayoutGrid.Row>
      </LayoutGrid>
      <Footer />
    </React.Fragment>
  );
}

function renderHeading(props: PasswordResetProps) {
  const { resetState } = props;
  let heading = '';
  let subtext = '';

  if (resetState.indexOf('request') === 0) {
    heading = l10n.t(
      'PasswordReset:Heading.title',
      "Don't worry, we all forget sometimes."
    );
    subtext = l10n.t(
      'PasswordReset:Heading.subtext',
      'To reset your password, enter the email address used ' +
        'to register your account.'
    );
  } else if (resetState.indexOf('reset') === 0) {
    heading = l10n.t('PasswordReset:Reset.reset', 'Reset password');
    subtext = l10n.t(
      'PasswordReset:Reset.resetbtxt',
      'Enter a new password for your Built account below.'
    );
  }

  return (
    <React.Fragment>
      <Heading size="xxl" className="margin-v-sm">
        {heading}
      </Heading>
      <p className={styles.lead}>{subtext}</p>
    </React.Fragment>
  );
}

function renderRequest(props: PasswordResetProps) {
  const { resetState, resetType } = props;
  const isAccepted = resetState === ResetState.REQUEST_ACCEPTED;
  const isErrored = resetState === ResetState.REQUEST_ERRORED;

  if (!isAccepted && !isErrored) {
    return renderEmailForm(props);
  } else if (isAccepted && resetType === ResetType.EMAIL) {
    return renderEmailResetComplete(props);
  } else {
    return (
      <React.Fragment>
        {renderError(props)}
        {renderBackButton()}
      </React.Fragment>
    );
  }
}

function renderEmailForm(props: PasswordResetProps) {
  const { resetState, code, gotoReset, requestReset } = props;
  const useMockCognito = getConfig('REACT_APP_USE_MOCK_COGNITO') === 'true';
  const isSubmitted = resetState === ResetState.REQUEST_SUBMITTED;

  return (
    <form
      onSubmit={code ? gotoReset : requestReset}
      className={[styles.verticalSpacing, styles.passwordForm].join(' ')}
    >
      {useMockCognito && (
        <Notification
          type="warning"
          title="Mock Cognito Enabled"
          className={styles.taLeft}
        >
          <p>
            {l10n.t(
              'PasswordReset:error.mock.notSupported',
              'Registration is not supported when mock cognito mode is enabled.'
            )}
          </p>
        </Notification>
      )}
      <EmailField
        autoFocus={true}
        disabled={isSubmitted}
        name="email"
        onChange={props.onEmailAddressChange}
        showError={props.showEmailError}
        value={props.email}
      />
      <ActionButton
        className={styles.resetPasswordButton}
        type="submit"
        loading={isSubmitted}
        label={
          code
            ? l10n.t('PasswordReset:Reset.continue', 'Continue')
            : l10n.t('PasswordReset:Reset.sendLink', 'Reset password')
        }
      />
    </form>
  );
}

function renderEmailResetComplete(props: PasswordResetProps) {
  const { resetState, email } = props;
  const isAccepted = resetState === ResetState.REQUEST_ACCEPTED;
  return (
    <React.Fragment>
      <Notification
        className={styles.notice}
        title={
          isAccepted
            ? l10n.t('PasswordReset:Reset.success', 'Success!')
            : l10n.t('PasswordReset:Reset.oops', 'Oops!')
        }
        type={isAccepted ? 'success' : 'danger'}
      >
        <p>
          {l10n.t('PasswordReset:Reset.emailsent1', `An email was sent to `)}
          <span className={styles.email}>{' ' + email + ' '}</span>
          {l10n.t(
            'PasswordReset:Reset.emailsent2',
            ' with a link to complete the password reset process. ' +
              "If you didn't receive this email, make sure you entered" +
              'the email address registered with your Built account.'
          )}
        </p>
      </Notification>
      {renderBackButton()}
    </React.Fragment>
  );
}

function renderMfaField(props: PasswordResetProps) {
  const {
    code,
    error,
    onChange,
    phoneNumber,
    requestNewCode,
    resetState
  } = props;
  const phoneSlice = phoneNumber!.slice(phoneNumber!.length - 4);
  const isSubmitted = resetState === ResetState.RESET_SUBMITTED;

  return (
    <Form.Group
      label={l10n.t('PasswordReset:mfa.verificationLabel', 'Verification code')}
      subtext={
        <React.Fragment>
          {l10n.t(
            'PasswordReset:mfa.verificationSubtext1',
            'Enter the code we just sent to the number ending in'
          )}{' '}
          <b>{phoneSlice}</b>{' '}
          {l10n.t(
            'PasswordReset:mfa.verificationSubtext2',
            "via SMS. Didn't receive a code?"
          )}{' '}
          <Button
            buttonType="link"
            disabled={isSubmitted}
            onClick={requestNewCode}
            label={l10n.t(
              'PasswordReset:mfa.requestNewCode',
              'Request a new code'
            )}
          />
          .
        </React.Fragment>
      }
    >
      <Input
        autoFocus={true}
        className={error ? styles.error : undefined}
        label=""
        name="code"
        value={code}
        onChange={onChange}
        size="lg"
        subtext={error ? error.message : undefined}
      />
    </Form.Group>
  );
}

function renderReset(props: PasswordResetProps) {
  const { resetState, resetType, resetPassword, password, onChange } = props;
  const isAccepted = resetState === ResetState.RESET_ACCEPTED;
  const isSubmitted = resetState === ResetState.RESET_SUBMITTED;
  const isErrored = resetState === ResetState.RESET_ERRORED;

  if (!isAccepted) {
    return (
      <form
        onSubmit={resetPassword}
        className={[styles.verticalSpacing, styles.passwordForm].join(' ')}
      >
        {resetType === ResetType.PHONE_NUMBER && renderMfaField(props)}
        <PasswordField
          password={password}
          onChange={onChange}
          className="ta-left margin-b-md"
        />

        <ActionButton
          type="submit"
          loading={isSubmitted}
          disabled={!passwordValid(password)}
          label={l10n.t('PasswordReset:Reset.myPassword', 'Reset my password')}
        />
      </form>
    );
  } else if (isErrored) {
    return (
      <React.Fragment>
        <Notification
          className={styles.notice}
          title={l10n.t(
            'PasswordReset:error.passwordResetErrorTitle',
            'Password reset error'
          )}
          type="danger"
        >
          <p>
            {l10n.t(
              'PasswordReset:error.passwordResetErrorMessage',
              'There was a problem submitting your reset request. Please try again in a few minutes.'
            )}
          </p>
        </Notification>
        {renderBackButton()}
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <Notification
          className={styles.notice}
          title={
            isAccepted
              ? l10n.t('PasswordReset:Reset.success', 'Success!')
              : l10n.t('PasswordReset:Reset.oops', 'Oops!')
          }
          type={isAccepted ? 'success' : 'danger'}
        >
          <p>
            {isAccepted
              ? l10n.t(
                  'PasswordReset:Reset.hasBeenReset',
                  'Your password has been reset.'
                )
              : l10n.t(
                  'PasswordReset:Reset.problem',
                  `There was a problem submitting your reset request. Please try again in a few minutes.`
                )}
          </p>
        </Notification>
        {renderBackButton()}
      </React.Fragment>
    );
  }
}

/**
 * Renders the appropriate error messages that we care about
 * during a password reset flow
 * @param props
 */
function renderError(props: PasswordResetProps) {
  const error = props.error as AuthError;

  switch (error.type) {
    case AuthErrorType.PASSWORD_RESET_REQUIRED_ERROR:
    case AuthErrorType.ATTEMPTS_EXCEEDED_ERROR:
      return ErrorNotifications.genericError(
        error,
        'danger',
        false,
        styles.notice
      );
    case AuthErrorType.ACCOUNT_DISABLED:
      return ErrorNotifications.accountDisabledError(error, styles.notice);
    case AuthErrorType.ACCOUNT_EXPIRED:
      return ErrorNotifications.accountExpiredError(error, styles.notice);
    case AuthErrorType.LIMIT_EXCEEDED_ERROR:
      return ErrorNotifications.passwordResetLimitError(error, styles.notice);
    case AuthErrorType.UNKNOWN_ERROR:
    default:
      return ErrorNotifications.unknownError(styles.notice);
  }
}

function renderBackButton() {
  return (
    <div className="ta-center">
      <ActionButton
        href="/"
        label={l10n.t(
          'EmailVerification:ActionButton.backbutton',
          'Back to Log In'
        )}
        className={styles.resetPasswordButton}
      />
    </div>
  );
}

export interface PasswordResetProps {
  /** The email to send the password reset email to */
  email: string;

  /** Is the email value a valid email address? */
  emailValid: boolean;

  /** The phone number the password was sent to */
  phoneNumber?: string;

  /** The current state of the password reset form */
  resetState: ResetState;

  /** The type of reset we're doing */
  resetType?: ResetType;

  /** The new password */
  password: string;

  /** Optional additional class name for the component */
  className?: string;

  /**
   * The SMS reset code, if using SMS instead of email to handle the reset
   * process
   */
  code?: string;

  /** The password reset error */
  error?: AuthError | Error;

  /** Whether to show inline validation error for email field */
  showEmailError: boolean;

  /** Callback to handle logging in */
  login(): void;

  /** Event handler for changes in the form's input elements */
  onChange(event: React.SyntheticEvent<HTMLInputElement>): void;

  /** Event handler for EmailField */
  onEmailAddressChange(email: string, emailValid: boolean): void;

  /** Requests a new SMS code be sent to the user */
  requestNewCode(event: React.SyntheticEvent<HTMLElement>): void;

  /** Event handler for when a password reset is requested */
  requestReset(event: React.SyntheticEvent<HTMLElement>): void;

  /** Event handler for when the password is reset */
  resetPassword(event: React.SyntheticEvent<HTMLElement>): void;

  /** Event handler for the 'Continue' button */
  gotoReset(event: React.SyntheticEvent<HTMLElement>): void;
}

export enum ResetState {
  REQUESTING = 'requesting',
  REQUEST_SUBMITTED = 'requestSubmitted',
  REQUEST_ACCEPTED = 'requestAccepted',
  REQUEST_ERRORED = 'requestErrored',
  RESETTING = 'resetting',
  RESET_SUBMITTED = 'resetSubmitted',
  RESET_ACCEPTED = 'resetAccepted',
  RESET_ERRORED = 'resetErrored'
}

export enum ResetType {
  EMAIL = 'email',
  PHONE_NUMBER = 'phone_number'
}



// WEBPACK FOOTER //
// ./src/components/PasswordReset.tsx